import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  getOrgCategoryPercentages,
  saveOrgCategoryPercentages,
} from "../api-services/superAdmin";

const PercentageDialog = ({ open, onClose, org, onSave }) => {
  const [servicesWithCategories, setServicesWithCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (categoryId, serviceId, value) => {
    setServicesWithCategories((prev) =>
      prev.map((item) =>
        item.categoryId === categoryId && item.serviceId === serviceId
          ? { ...item, percentage: Number(value) }
          : item
      )
    );
  };

  useEffect(() => {
    if (open && org?.id) {
      const fetchData = async () => {
        try {
          setLoading(true);

          // ✅ Directly fetch categories + services + percentages
          const percentagesRes = await getOrgCategoryPercentages(org.id);
          const uiPercentages = percentagesRes?.percentages || [];

          setServicesWithCategories(uiPercentages);
        } catch (err) {
          console.error("Error fetching data:", err);
          setServicesWithCategories([]);
          setSnackbar({
            open: true,
            message: "Failed to load data.",
            severity: "error",
          });
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    } else {
      setServicesWithCategories([]);
    }
  }, [open, org]);

  const handleSubmit = async () => {
    try {
      // ✅ Only include rows with services
      const payload = servicesWithCategories
        .filter((item) => item.hasServices)
        .map((item) => ({
          categoryId: item.categoryId,
          serviceId: item.serviceId,
          percentage: item.percentage,
        }));

      await saveOrgCategoryPercentages(org.id, payload);

      setSnackbar({
        open: true,
        message: "Percentages saved successfully!",
        severity: "success",
      });

      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error("Error saving percentages:", err);
      setSnackbar({
        open: true,
        message: "Failed to save percentages.",
        severity: "error",
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{org?.name} Services Percentage</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && servicesWithCategories.length === 0 && (
          <Typography>No services found for assigned categories.</Typography>
        )}

        {servicesWithCategories.map((item) => (
          <Box
            key={item.id}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            my={1}
            sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography sx={{ fontWeight: 500, flex: 1 }}>
              {item.display}
            </Typography>
            {item.hasServices ? (
              <>
                <TextField
                  type="number"
                  variant="outlined"
                  size="small"
                  value={item.percentage}
                  onChange={(e) =>
                    handleChange(item.categoryId, item.serviceId, e.target.value)
                  }
                  sx={{ width: "100px" }}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                />
                <Typography sx={{ ml: 1 }}>%</Typography>
              </>
            ) : (
              <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
                No services assigned
              </Typography>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={servicesWithCategories.length === 0 || loading}
        >
          Save
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default PercentageDialog;
